import { Alert, Button, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { Text } from "@src/appV2/redesign/components/Text";
import { useQueryClient } from "@tanstack/react-query";
import { addHours } from "date-fns";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { getCaseQueryKey } from "../api/useCase";
import {
  useFormatTimestampForWorkplace,
  useFormatWorkplaceDateInput,
} from "../api/useFormatTimestampForWorkplace";
import { useUpdateVisit } from "../api/useUpdateVisit";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import { getHomeHealthCaseMobileDetailPath } from "../paths";
import { isHourlyPricingAllowed, PricingType, type Visit, VisitType } from "../types";
import { MobileAddVisitStep3, type AddVisitStep3Data } from "./MobileAddVisitStep3";
import { MobileHomeHealthPageHeader } from "./MobileHomeHealthPageHeader";

interface EditVisitFormData extends AddVisitStep3Data {
  pricingType: PricingType;
}

const buttonBaseSx = {
  flex: 1,
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

function formatRateInput(rate: Visit["attributes"]["payRate"]): string {
  return rate ? (rate.amountInMinorUnits / 100).toString() : "";
}

interface Props {
  visit: Visit;
  patientAddress: string;
}

export function MobileEditVisitForm({ visit, patientAddress }: Props) {
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  const { attributes } = visit;
  const visitType = attributes.type;
  const { caseId } = attributes;

  // Hourly pricing is only valid for REGULAR non-PT visits (mirrors desktop).
  // The visit's type and worker requirement are fixed here, so this never
  // changes; a visit that can't be hourly hides the toggle and is treated as
  // per-visit (coercing any stale per-hour value below).
  const allowHourly = isHourlyPricingAllowed({
    visitType: attributes.type,
    workerReq: attributes.workerReq,
  });

  const formatTimestampForWorkplace = useFormatTimestampForWorkplace();
  const formatWorkplaceDateInput = useFormatWorkplaceDateInput();

  const [initialFormData] = useState<EditVisitFormData>(() => ({
    pricingType: allowHourly ? attributes.pricingType : PricingType.PER_VISIT,
    visitsPerWeek: attributes.visitsPerWeek?.toString() ?? "",
    totalWeeks: attributes.durationInWeeks?.toString() ?? "",
    firstVisitDueDate: attributes.deadline ? formatWorkplaceDateInput(attributes.deadline) : "",
    estimatedDuration: attributes.estimatedDuration?.toString() ?? "",
    chargePerVisit: formatRateInput(attributes.chargeRate),
    payPerVisit: formatRateInput(attributes.payRate),
    modificationReason: "",
  }));
  const [formData, setFormData] = useState<EditVisitFormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: updateVisit } = useUpdateVisit(visit.id);
  // Local flag rather than the mutation `isLoading`: that reads false again once
  // the mutation resolves but before navigation (during query invalidation),
  // briefly re-enabling Save and allowing a duplicate submit.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    formData.modificationReason
  );

  function handleChange(field: keyof EditVisitFormData, value: string | PricingType) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function parseDateFieldToISO(dateString: string): string {
    const [yearStr, monthStr, dayStr] = dateString.split("-");
    const localDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
    return formatTimestampForWorkplace(addHours(localDate, 12));
  }

  const isRegularVisit = visitType === VisitType.REGULAR;

  // Each flag means "changed AND sendable". A blank/invalid value can't be
  // persisted by this PATCH (number/rate fields are omitted, never nulled), so
  // a cleared field must not count as a change — otherwise Save would enable and
  // the user would think the value was cleared when nothing was sent. Reusing
  // these same flags for the payload and the analytics event keeps all three in
  // sync (no logging a field as updated that wasn't actually sent).
  const isPricingTypeChanged = formData.pricingType !== initialFormData.pricingType;
  // Estimated duration only applies to hourly visits and, like desktop, must be
  // at least 2 hours. An hourly visit missing a valid duration blocks Save.
  const isHourly = formData.pricingType === PricingType.PER_HOUR;
  const estimatedDurationNumber = Number.parseFloat(formData.estimatedDuration);
  const isEstimatedDurationValid =
    !isHourly || (Number.isFinite(estimatedDurationNumber) && estimatedDurationNumber >= 2);
  const willUpdateEstimatedDuration =
    isHourly &&
    formData.estimatedDuration !== initialFormData.estimatedDuration &&
    Number.isFinite(estimatedDurationNumber);
  const willUpdateVisitsPerWeek =
    isRegularVisit &&
    formData.visitsPerWeek !== initialFormData.visitsPerWeek &&
    formData.visitsPerWeek !== "";
  const willUpdateTotalWeeks =
    isRegularVisit &&
    formData.totalWeeks !== initialFormData.totalWeeks &&
    formData.totalWeeks !== "";
  const willUpdateDeadline =
    formData.firstVisitDueDate !== initialFormData.firstVisitDueDate &&
    formData.firstVisitDueDate !== "";
  const willUpdateCharge =
    formData.chargePerVisit !== initialFormData.chargePerVisit &&
    Number.isFinite(Number.parseFloat(formData.chargePerVisit));
  const willUpdatePay =
    formData.payPerVisit !== initialFormData.payPerVisit &&
    Number.isFinite(Number.parseFloat(formData.payPerVisit));

  const hasChanges =
    isPricingTypeChanged ||
    willUpdateEstimatedDuration ||
    willUpdateVisitsPerWeek ||
    willUpdateTotalWeeks ||
    willUpdateDeadline ||
    willUpdateCharge ||
    willUpdatePay;

  async function handleSubmit() {
    setSubmitError(null);
    setIsSubmitting(true);
    const { modificationReason } = formData;
    const updatedFields: string[] = [];

    try {
      await updateVisit({
        data: {
          attributes: {
            ...(isPricingTypeChanged ? { pricingType: formData.pricingType } : {}),
            ...(willUpdateEstimatedDuration ? { estimatedDuration: estimatedDurationNumber } : {}),
            ...(willUpdateVisitsPerWeek
              ? { visitsPerWeek: Number.parseInt(formData.visitsPerWeek, 10) }
              : {}),
            ...(willUpdateTotalWeeks
              ? { durationInWeeks: Number.parseInt(formData.totalWeeks, 10) }
              : {}),
            ...(willUpdateDeadline
              ? { deadline: parseDateFieldToISO(formData.firstVisitDueDate) }
              : {}),
            ...(willUpdateCharge
              ? {
                  chargeRate: {
                    amountInMinorUnits: Math.round(
                      Number.parseFloat(formData.chargePerVisit) * 100
                    ),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
            ...(willUpdatePay
              ? {
                  payRate: {
                    amountInMinorUnits: Math.round(Number.parseFloat(formData.payPerVisit) * 100),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
            modificationReason,
          },
          type: "visit",
        },
      });

      if (isPricingTypeChanged) {
        updatedFields.push("pricingType");
      }
      if (willUpdateEstimatedDuration) {
        updatedFields.push("estimatedDuration");
      }
      if (willUpdateVisitsPerWeek) {
        updatedFields.push("visitsPerWeek");
      }
      if (willUpdateTotalWeeks) {
        updatedFields.push("durationInWeeks");
      }
      if (willUpdateDeadline) {
        updatedFields.push("deadline");
      }
      if (willUpdateCharge) {
        updatedFields.push("chargeRate");
      }
      if (willUpdatePay) {
        updatedFields.push("payRate");
      }
      logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_UPDATED, {
        workplaceId,
        type: attributes.type,
        workerReq: attributes.workerReq,
        visitId: visit.id,
        bookedWorkerId: attributes.bookedWorkerId,
        caseId,
        updatedFields,
        performedByAdmin: isAdmin,
      });

      // The case detail page reads its visit list from the case query, which the
      // visit mutation does not invalidate on its own (mirrors the Add Visit wizard).
      await queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId }));
      history.replace(getHomeHealthCaseMobileDetailPath(caseId));
    } catch {
      setSubmitError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  }

  const paddingX = 3;

  return (
    <Stack
      sx={{
        // Fill (not exceed) the fixed-height, overflow-hidden page wrapper so the
        // header and footer stay pinned and only the middle section scrolls.
        // minHeight:"100vh" let tall content grow the column past the clipped
        // parent, pushing the footer (Save) out of view on small screens.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.baseDark",
        paddingBottom: "4rem",
      }}
    >
      <Stack sx={{ px: paddingX, pt: 4, gap: 3 }}>
        <MobileHomeHealthPageHeader title="Edit visit" />
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: paddingX, py: 3 }} spacing={3}>
        {submitError && <Alert severity="error">{submitError}</Alert>}

        {allowHourly && (
          <Stack spacing={0.5}>
            <Text semibold variant="body1">
              Type
            </Text>
            <Text variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              Paid per visit or per hour
            </Text>
            <ToggleButtonGroup
              value={formData.pricingType}
              exclusive
              onChange={(_, newValue: PricingType | null) => {
                if (newValue !== null) {
                  handleChange("pricingType", newValue);
                }
              }}
              sx={{ mt: 1 }}
            >
              <ToggleButton value={PricingType.PER_VISIT}>Per visit</ToggleButton>
              <ToggleButton value={PricingType.PER_HOUR}>Per hour</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        <MobileAddVisitStep3
          data={formData}
          pricingType={formData.pricingType}
          visitType={visitType}
          workerRequirement={attributes.workerReq}
          patientAddress={patientAddress}
          onChange={handleChange}
        />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ px: paddingX, pb: 4, pt: 2 }}>
        <Button
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace(getHomeHealthCaseMobileDetailPath(caseId));
            }
          }}
          sx={{ ...buttonBaseSx }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            isSubmitting ||
            !hasChanges ||
            !isEstimatedDurationValid ||
            !isModificationReasonSatisfied
          }
          onClick={() => {
            void handleSubmit();
          }}
          sx={{ ...buttonBaseSx }}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}
